import { z } from 'zod';
import { Uuid } from './blocks.js';
export const ASSET_UPLOAD_MAX_BYTES = 16 * 1024 * 1024;
export const AssetSourceType = z.enum(['Bundled', 'External', 'R2']);
const BundledPublicPath = z.string().refine((value) => {
    if (!value.startsWith('/') || value.startsWith('//'))
        return false;
    if (/[\u0000-\u001f\u007f\\?#%]/.test(value))
        return false;
    if (/^\/(?:https?:|data:|blob:|file:)/i.test(value))
        return false;
    if (value.split('/').some((segment) => segment === '.' || segment === '..'))
        return false;
    if (value.slice(1).includes('//'))
        return false;
    return /\.(?:avif|gif|jpe?g|png|svg|webp)$/i.test(value);
}, 'Expected a safe absolute bundled media path');
export const ReleasedAsset = z
    .object({
    assetId: Uuid,
    sourceType: AssetSourceType,
    alt: z.string().min(1),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    format: z.enum(['jpeg', 'webp']),
    variants: z.array(Uuid).min(1).max(3).optional(),
    provenance: z
        .string()
        .url()
        .refine((value) => new URL(value).protocol === 'https:', 'Expected an HTTPS URL')
        .optional(),
    pinned: z.discriminatedUnion('kind', [
        z
            .object({
            kind: z.literal('Bundled'),
            publicPath: BundledPublicPath,
            checksum: z.string().min(1),
        })
            .strict(),
        z
            .object({
            kind: z.literal('R2'),
            objectKey: z.string().min(1),
            checksum: z.string().min(1),
        })
            .strict(),
    ]),
})
    .strict();
export const PreparedImageFormat = z.enum(['jpeg', 'webp']);
export const PreparedImageContentType = z.enum(['image/jpeg', 'image/webp']);
export const AssetUploadStatus = z.enum(['Pending', 'Uploading', 'Ready', 'Failed']);
export const SignAssetUploadRequest = z
    .object({
    filename: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .regex(/^[^/\\\u0000-\u001f\u007f]+$/, 'Expected a plain filename'),
    contentType: PreparedImageContentType,
    byteSize: z.number().int().positive(),
    checksum: z.string().regex(/^[0-9a-f]{64}$/),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    alt: z.string().trim().min(1).max(500),
})
    .strict();
export const AssetUsageBadges = z.object({
    draft: z.number().int().nonnegative(),
    releases: z.number().int().nonnegative(),
});
export const AdminAsset = z
    .object({
    id: Uuid,
    sourceType: AssetSourceType,
    originalFilename: z.string().min(1),
    mimeType: z.string().min(1),
    byteSize: z.number().int().positive().nullable(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    format: PreparedImageFormat.nullable(),
    alt: z.string().min(1),
    status: AssetUploadStatus,
    checksum: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable(),
    uploadedBy: Uuid.nullable(),
    revision: z.number().int().nonnegative(),
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    usage: AssetUsageBadges,
})
    .strict();
export const AssetUploadContract = z
    .object({
    mode: z.enum(['Direct', 'Proxy']),
    assetId: Uuid,
    nonce: Uuid,
    objectKey: z.string().regex(/^media\/[0-9a-f-]+\.(?:jpg|webp)$/),
    uploadUrl: z.string().min(1),
    requiredHeaders: z.record(z.string(), z.string()),
    expiresAt: z.iso.datetime(),
})
    .strict();
export const SignAssetUploadResponse = z.object({ upload: AssetUploadContract }).strict();
export const ConfirmAssetUploadRequest = z
    .object({
    assetId: Uuid,
    nonce: Uuid,
    objectKey: z.string().regex(/^media\/[0-9a-f-]+\.(?:jpg|webp)$/),
    checksum: z.string().regex(/^[0-9a-f]{64}$/),
})
    .strict();
export const ConfirmAssetUploadResponse = z
    .object({ asset: AdminAsset, idempotent: z.boolean() })
    .strict();
const ResponsiveAssetVariant = z
    .object({ assetId: Uuid, width: z.number().int().positive() })
    .strict();
export const RegisterAssetVariantsRequest = z
    .object({ variants: z.array(ResponsiveAssetVariant).min(2).max(3) })
    .strict()
    .superRefine((value, context) => {
    const assetIds = value.variants.map((variant) => variant.assetId);
    const widths = value.variants.map((variant) => variant.width);
    if (new Set(assetIds).size !== assetIds.length) {
        context.addIssue({ code: 'custom', path: ['variants'], message: 'Asset IDs must be unique' });
    }
    if (new Set(widths).size !== widths.length) {
        context.addIssue({ code: 'custom', path: ['variants'], message: 'Widths must be unique' });
    }
});
export const RegisterAssetVariantsResponse = z
    .object({
    canonicalAssetId: Uuid,
    variants: z.array(Uuid).min(2).max(3),
    idempotent: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (!value.variants.includes(value.canonicalAssetId)) {
        context.addIssue({
            code: 'custom',
            path: ['variants'],
            message: 'Responsive variants must include the canonical asset',
        });
    }
});
export const AssetListQuery = z
    .object({
    search: z.string().trim().max(100).optional(),
    cursor: Uuid.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    archived: z.enum(['exclude', 'include', 'only']).default('exclude'),
})
    .strict();
export const AdminAssetsResponse = z
    .object({ assets: z.array(AdminAsset), nextCursor: Uuid.nullable() })
    .strict();
export const AdminAssetResponse = z.object({ asset: AdminAsset }).strict();
export const AssetUsageLocation = z
    .object({
    id: Uuid,
    area: z.enum(['Settings', 'Page', 'Pricing', 'Shoot', 'Journal']),
    entityId: Uuid,
    label: z.string().min(1),
    context: z.string().min(1),
    purpose: z.enum(['Cover', 'Gallery', 'Pricing', 'Journal', 'Hero', 'Feature', 'Seo', 'Brand']),
    position: z.number().int().nonnegative(),
    href: z.string().startsWith('/'),
})
    .strict();
export const AssetUsageLocationsResponse = z
    .object({ usages: z.array(AssetUsageLocation) })
    .strict();
export const UpdateAssetRequest = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    alt: z.string().trim().min(1).max(500),
})
    .strict();
export const ArchiveAssetRequest = z
    .object({ expectedRevision: z.number().int().nonnegative() })
    .strict();
